import { sendErrorMetric } from "@expert/cloudwatch-metrics";
import { useCallDetailsStore, useProductOfferStore, useVerifiedCustomerStore } from "@expert/sales-tools";
import { Container, Stack, Text } from "@mantine/core";
import { useEffect, useMemo } from "react";

import { useSetGaiaSmartOfferChecklistContext } from "@/components/workspace/hooks/useSetGaiaSmartOfferChecklistContext";
import { IneligibleIcon, LoadingAnimation } from "@/sales-tools/home-product/components/Icons";
import { useEligibility } from "@/sales-tools/home-product/hooks/useEligibility";
import { useProductOfferPolling } from "@/sales-tools/home-product/hooks/useProductOfferPolling";
import { useQuerySelectedProductOffer } from "@/sales-tools/home-product/hooks/useQuerySelectedProductOffer";
import { getSessionContextKeysFromProducts } from "@/sales-tools/home-product/utils/getSessionContextKeysFromProducts";
import { useAgentAttributesStore } from "@/sales-tools/stores/agent-attributes.store";
import { getReservationSid, parsePhoneNumberAttributes } from "@/sales-tools/utils";
import { isSimulatedVoiceTask, useLogger, usePartner, useSession, useSessionId, useTypedMostRecentTask } from "@/sdk";

import classes from "./CustomerOffers.module.css";
import { CustomerOffersLoaded } from "./CustomerOffersLoaded";

export function CustomerOffers() {
    const logger = useLogger({ module: "CustomerOffers", supportTeam: "sales" });
    const { startedTimestamp } = useSession();

    useQuerySelectedProductOffer();
    useProductOfferPolling();

    const { task: mostRecentTask, isVoice, isChat, chatTask, customerInteractionId } = useTypedMostRecentTask();
    const partner = usePartner();
    const isExpertUniversitySimulatedVoiceTask = isSimulatedVoiceTask(mostRecentTask);

    if (!isVoice && !isChat) {
        throw new Error("Active task is not voice or chat task");
    }

    const { CLIENT, PROTECTED_SALES } = useCallDetailsStore.getState().callDetails;

    const sessionId = useSessionId();
    const customer = useVerifiedCustomerStore((state) => state.getVerifiedCustomer(sessionId).customer);

    const storesHaveFinishedRehydrating =
        useProductOfferStore.persist.hasHydrated() && useCallDetailsStore.persist.hasHydrated();
    const storesHaveRehydratedData = useProductOfferStore((state) => (state.getProducts(sessionId)?.length ?? 0) > 0);
    const isCustomerAccountNumberSet = Boolean(customer?.accountNumber);
    const hasCurrentSubscriptions = (customer?.currentSubscriptions?.length ?? 0) > 0;
    const hasEligibilityAttributes = Boolean(
        customer?.productName ||
        customer?.metadata?.isInternationalCaller ||
        (isChat && chatTask?.sourceApp) ||
        (customer?.currentSubscriptions?.length ?? 0) > 0 ||
        useAgentAttributesStore.getState().site ||
        CLIENT,
    );
    const shouldRefetchEligibilityForSession =
        (storesHaveFinishedRehydrating && !storesHaveRehydratedData) ||
        isCustomerAccountNumberSet ||
        hasCurrentSubscriptions ||
        hasEligibilityAttributes;

    const isCustomerInProtectedState = PROTECTED_SALES === "1";
    const {
        data: checkEligibilityResult,
        error,
        isError: eligibilityError,
        isLoading: isLoadingEligibility,
    } = useEligibility(
        {
            customer: {
                accountNumber: customer?.accountNumber || "",
                email: customer?.email,
            },
            customerInteractionId,
            partner,
            reservationSid: getReservationSid(mostRecentTask) ?? sessionId,
            sessionId,
            ...(isExpertUniversitySimulatedVoiceTask
                ? { customerMdn: customer?.mdn }
                : { ...parsePhoneNumberAttributes(customer?.mdn) }),
            address: customer?.address,
            eligibilityAttributes: {
                currentPlan: customer?.productName,
                isInternationalCaller: customer?.metadata?.isInternationalCaller,
                chatSourceApp: isChat ? chatTask?.sourceApp : undefined,
                currentSubscriptions: customer?.currentSubscriptions,
                expertLocation: useAgentAttributesStore.getState().site,
                isCustomerInProtectedState,
                client: CLIENT,
            },
            salesChannel: mostRecentTask?.type,
        },
        shouldRefetchEligibilityForSession,
    );

    const sessionContextKeys = useMemo(
        () => getSessionContextKeysFromProducts(logger, checkEligibilityResult?.productConfigs ?? []),
        [checkEligibilityResult?.productConfigs, logger],
    );
    useSetGaiaSmartOfferChecklistContext(sessionContextKeys);

    useEffect(() => {
        if (shouldRefetchEligibilityForSession && checkEligibilityResult) {
            useProductOfferStore
                .getState()
                .updateProducts(sessionId, checkEligibilityResult.productConfigs, checkEligibilityResult.displayCues);
        }
    }, [checkEligibilityResult, sessionId, shouldRefetchEligibilityForSession]);

    useEffect(() => {
        if (eligibilityError) {
            logger.warn({ error }, `Error checking eligibility FE`);
            void sendErrorMetric("EligibilityError", { eventSource: "Eligibility-FE" });
        }
    }, [eligibilityError, error, logger]);

    if (eligibilityError) {
        return (
            <Stack align={"center"} data-testid="failure" mt={"sm"}>
                <Container>{IneligibleIcon}</Container>
                <Text className={classes.statusBodyHeader}>We had an issue checking eligibility.</Text>
                <Text className={classes.statusBodyText} data-testid="sale-view-status-body">
                    Try refreshing the page.
                </Text>
            </Stack>
        );
    }

    if (isLoadingEligibility) {
        return (
            <Stack align={"center"} mt={"sm"}>
                <Text size="sm">Confirming customer information...</Text>
                <Container maw="160px">
                    <LoadingAnimation />
                </Container>
            </Stack>
        );
    }

    return (
        <CustomerOffersLoaded
            startedTimestamp={startedTimestamp}
            isChat={isChat}
            isExpertUniversitySimulatedVoiceTask={isExpertUniversitySimulatedVoiceTask}
        />
    );
}
